import React, { useState } from 'react';
import {
    Box,
    Typography,
    Grid,
    Paper,
    Divider,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Chip,
    Card,
    CardContent,
    Link,
    Button,
    Avatar
} from '@mui/material';

import {
    VolumeUp,
    Build,
    ReportProblem,
    TipsAndUpdates,
    CheckCircle,
    ArrowRight,
    Home,
    DoorFront,
    MeetingRoom
} from '@mui/icons-material';

// スクロール用の関数
const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
        const offset = 80; // ヘッダーの高さ分のオフセット
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - offset;

        window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
        });
    }
};

const PostContent = () => {
    const [showToc, setShowToc] = useState(true);
    return (
        <Box sx={{ maxWidth: 800, mx: 'auto', px: { xs: 1, sm: 3 } }}>
            {showToc ? (
                <Card sx={{ mb: 4, boxShadow: 2, bgcolor: '#e3f2fd', border: '1px solid #ddd', borderRadius: 2 }}>
                    <CardContent sx={{ p: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <Typography variant="h6" sx={{ flexGrow: 1 }}>目次</Typography>
                            <Button variant="outlined" size="small" onClick={() => setShowToc(false)}>閉じる</Button>
                        </Box>
                        <List sx={{ p: 0 }}>
                            <ListItem sx={{ p: 0.5, display: 'flex', alignItems: 'center' }}>
                                <Box sx={{ minWidth: 32, minHeight: 32, bgcolor: '#f5f5f5', color: '#888', fontWeight: 'bold', borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', mr: 1, fontSize: '1.1rem' }}>
                                    1.
                                </Box>
                                <Link component="button" onClick={() => scrollToSection('introduction')} color="inherit" underline="hover" sx={{ cursor: 'pointer', fontSize: '0.95rem' }}>
                                    はじめに：ルームシェア防音対策の重要性
                                </Link>
                            </ListItem>
                            <ListItem sx={{ p: 0.5, display: 'flex', alignItems: 'center' }}>
                                <Box sx={{ minWidth: 32, minHeight: 32, bgcolor: '#f5f5f5', color: '#888', fontWeight: 'bold', borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', mr: 1, fontSize: '1.1rem' }}>
                                    2.
                                </Box>
                                <Link component="button" onClick={() => scrollToSection('goods')} color="inherit" underline="hover" sx={{ cursor: 'pointer', fontSize: '0.95rem' }}>
                                    おすすめ防音グッズ8選
                                </Link>
                            </ListItem>
                            <ListItem sx={{ p: 0.5, display: 'flex', alignItems: 'center' }}>
                                <Box sx={{ minWidth: 32, minHeight: 32, bgcolor: '#f5f5f5', color: '#888', fontWeight: 'bold', borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', mr: 1, fontSize: '1.1rem' }}>
                                    3.
                                </Box>
                                <Link component="button" onClick={() => scrollToSection('diy')} color="inherit" underline="hover" sx={{ cursor: 'pointer', fontSize: '0.95rem' }}>
                                    DIYでできる防音アイデア
                                </Link>
                            </ListItem>
                            <ListItem sx={{ p: 0.5, display: 'flex', alignItems: 'center' }}>
                                <Box sx={{ minWidth: 32, minHeight: 32, bgcolor: '#f5f5f5', color: '#888', fontWeight: 'bold', borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', mr: 1, fontSize: '1.1rem' }}>
                                    4.
                                </Box>
                                <Link component="button" onClick={() => scrollToSection('troubles')} color="inherit" underline="hover" sx={{ cursor: 'pointer', fontSize: '0.95rem' }}>
                                    よくあるトラブルと防音の失敗例
                                </Link>
                            </ListItem>
                            <ListItem sx={{ p: 0.5, display: 'flex', alignItems: 'center' }}>
                                <Box sx={{ minWidth: 32, minHeight: 32, bgcolor: '#f5f5f5', color: '#888', fontWeight: 'bold', borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', mr: 1, fontSize: '1.1rem' }}>
                                    5.
                                </Box>
                                <Link component="button" onClick={() => scrollToSection('tips')} color="inherit" underline="hover" sx={{ cursor: 'pointer', fontSize: '0.95rem' }}>
                                    防音対策を最大限活かすコツ
                                </Link>
                            </ListItem>
                            <ListItem sx={{ p: 0.5, display: 'flex', alignItems: 'center' }}>
                                <Box sx={{ minWidth: 32, minHeight: 32, bgcolor: '#f5f5f5', color: '#888', fontWeight: 'bold', borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', mr: 1, fontSize: '1.1rem' }}>
                                    6.
                                </Box>
                                <Link component="button" onClick={() => scrollToSection('summary')} color="inherit" underline="hover" sx={{ cursor: 'pointer', fontSize: '0.95rem' }}>
                                    まとめ：快適なルームシェアのために
                                </Link>
                            </ListItem>
                        </List>
                    </CardContent>
                </Card>
            ) : (
                <Button
                    variant="outlined"
                    size="small"
                    onClick={() => setShowToc(true)}
                    sx={{ mb: 2 }}
                >
                    目次を表示
                </Button>
            )}

            {/* 導入 */}
            <Box id="introduction" sx={{ mb: 5 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    はじめに：ルームシェア防音対策の重要性
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                    ルームシェアは経済的で楽しい暮らし方ですが、気になるのが「音の問題」。生活リズムや文化の違い、オンライン会議や趣味の音──日々さまざまな音が交錯します。<b>防音対策</b>は、快適な共生のための必須ポイント。この記事では、<Chip label="おすすめ防音グッズ" color="primary" size="small" variant="outlined" sx={{ mr: 1 }} />や<Chip label="DIY防音" color="primary" size="small" variant="outlined" />の知恵、<Chip label="トラブル事例" color="primary" size="small" variant="outlined" sx={{ mr: 1, ml: 1 }} />
                    、<Chip label="効果的な使い方" color="primary" size="small" variant="outlined" />まで、実践的な情報を詳しくご紹介します。専門家目線のアドバイスも交えながら、あなたのルームシェア生活をもっと快適に！
                </Typography>
                <Box sx={{ width: '100%', my: 2 }}>
                    <img
                        src="/images/blog/living-sound_roomshare-soundproof-goods-ideas.jpg"
                        alt="明るい共有リビングでルームメイトが談笑し、柔らかいカーテンや家具が音を吸収している落ち着いた雰囲気の様子。色調は自然光に包まれ、心地よく静かな空間が表現されている"
                        style={{ width: '100%', borderRadius: 8, marginBottom: 8 }}
                    />
                </Box>
                <Divider sx={{ my: 2 }} />
            </Box>

            {/* 防音グッズ */}
            <Box id="goods" sx={{ mb: 5 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    おすすめ防音グッズ8選
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                    まずは「手軽さ」「効果」「コスパ」を重視した防音アイテム。専門家の視点から、ルームシェアに特におすすめのグッズを厳選しました。
                </Typography>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                        <Card sx={{ maxWidth: 345 }}>
                            <CardContent>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                    <VolumeUp sx={{ color: 'primary.main', mr: 1 }} />
                                    <Typography variant="h6" sx={{ flexGrow: 1 }}>
                                        防音カーテン
                                    </Typography>
                                </Box>
                                <Typography variant="body2">
                                    厚手の防音カーテンは、<b>窓</b>から入る騒音や外への漏れをしっかりカット。デザインも多彩で、インテリアにもなじみます。
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Card sx={{ maxWidth: 345 }}>
                            <CardContent>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                    <MeetingRoom sx={{ color: 'primary.main', mr: 1 }} />
                                    <Typography variant="h6" sx={{ flexGrow: 1 }}>
                                        ドア用隙間テープ
                                    </Typography>
                                </Box>
                                <Typography variant="body2">
                                    部屋のドアや引き戸の隙間に貼るだけ。音漏れの大半はここから起きるため、<b>コスパ最強</b>の対策です。
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Card sx={{ maxWidth: 345 }}>
                            <CardContent>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                    <Home sx={{ color: 'primary.main', mr: 1 }} />
                                    <Typography variant="h6" sx={{ flexGrow: 1 }}>
                                        ジョイントマット
                                    </Typography>
                                </Box>
                                <Typography variant="body2">
                                    フローリングに敷くだけで、<b>足音や物音の軽減</b>に。賃貸でも安心の簡単設置。
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Card sx={{ maxWidth: 345 }}>
                            <CardContent>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                    <DoorFront sx={{ color: 'primary.main', mr: 1 }} />
                                    <Typography variant="h6" sx={{ flexGrow: 1 }}>
                                        ドア下用ドラフトストッパー
                                    </Typography>
                                </Box>
                                <Typography variant="body2">
                                    ドアの下に差し込むだけで隙間をふさぎ、<b>話し声や生活音</b>の漏れを防止。冷暖房効率もUPします。
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Card sx={{ maxWidth: 345 }}>
                            <CardContent>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                    <VolumeUp sx={{ color: 'primary.main', mr: 1 }} />
                                    <Typography variant="h6" sx={{ flexGrow: 1 }}>
                                        吸音パネル（フォーム）
                                    </Typography>
                                </Box>
                                <Typography variant="body2">
                                    壁や天井に貼るだけ。<b>反響音や会話音</b>を和らげて、部屋全体の「響きすぎ」を抑えます。
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Card sx={{ maxWidth: 345 }}>
                            <CardContent>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                    <Build sx={{ color: 'primary.main', mr: 1 }} />
                                    <Typography variant="h6" sx={{ flexGrow: 1 }}>
                                        防音シート
                                    </Typography>
                                </Box>
                                <Typography variant="body2">
                                    床や壁に敷くだけで、重低音や足音の伝わりをカット。<b>賃貸でも原状回復OK</b>な商品も多いです。
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Card sx={{ maxWidth: 345 }}>
                            <CardContent>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                    <TipsAndUpdates sx={{ color: 'primary.main', mr: 1 }} />
                                    <Typography variant="h6" sx={{ flexGrow: 1 }}>
                                        ホワイトノイズマシン
                                    </Typography>
                                </Box>
                                <Typography variant="body2">
                                    小型スピーカーで「サーッ」という<em>ホワイトノイズ</em>を流し、周囲の音を気になりにくく。<b>勉強・就寝時</b>に活躍。
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Card sx={{ maxWidth: 345 }}>
                            <CardContent>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                    <CheckCircle sx={{ color: 'primary.main', mr: 1 }} />
                                    <Typography variant="h6" sx={{ flexGrow: 1 }}>
                                        耳栓・ノイズキャンセリングイヤホン
                                    </Typography>
                                </Box>
                                <Typography variant="body2">
                                    個人でできる最強防音。<b>どうしても防げない音</b>にはこれ。コンパクトで外出先でも使えます。
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
                <Box sx={{ width: '100%', my: 3 }}>
                    
                </Box>
                <Divider sx={{ my: 2 }} />
            </Box>

            {/* DIY防音 */}
            <Box id="diy" sx={{ mb: 5 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    DIYでできる防音アイデア
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                    市販グッズ以外にも、ちょっとした工夫やDIYで「音ストレス」を軽減できます。<b>賃貸OK</b>なテクニック中心にご紹介します。
                </Typography>
                <List>
                    <ListItem>
                        <ListItemIcon>
                            <Build color="primary" />
                        </ListItemIcon>
                        <ListItemText
                            primary="家具の配置で音をブロック"
                            secondary="クローゼットや本棚、ベッドを壁際に寄せて「防音壁」として活用。とくに隣室との間に配置するのが効果的です。"
                        />
                    </ListItem>
                    <ListItem>
                        <ListItemIcon>
                            <Build color="primary" />
                        </ListItemIcon>
                        <ListItemText
                            primary="カーテン2重掛け作戦"
                            secondary="既存のカーテンにもう一枚、遮音性のある布やカーテンを重ねて吊るすだけで遮音性UP。"
                        />
                    </ListItem>
                    <ListItem>
                        <ListItemIcon>
                            <Build color="primary" />
                        </ListItemIcon>
                        <ListItemText
                            primary="100均アイテムで隙間ふさぎ"
                            secondary="スポンジやモール、クッションテープをドアや窓の隙間に貼るだけ。低コスト・短時間でできます。"
                        />
                    </ListItem>
                    <ListItem>
                        <ListItemIcon>
                            <Build color="primary" />
                        </ListItemIcon>
                        <ListItemText
                            primary="ラグやカーペットの多用"
                            secondary="床に厚手のラグを敷くことで、足音や生活音が響きにくくなります。"
                        />
                    </ListItem>
                </List>
                <Box sx={{ width: '100%', my: 3 }}>
                    <img
                        src="/images/blog/diy-sound_roomshare-soundproof-goods-ideas.jpg"
                        alt="本棚やラグ、2重カーテンなどで賃貸部屋をアレンジし、生活音を効果的に遮断しているDIY防音の事例。明るい色合いの家具と工夫された配置がポイント"
                        style={{ width: '100%', borderRadius: 8 }}
                    />
                </Box>
                <Divider sx={{ my: 2 }} />
            </Box>

            {/* トラブル事例 */}
            <Box id="troubles" sx={{ mb: 5 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    よくあるトラブルと防音の失敗例
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                    防音対策をしても、「思ったより効果がない」「かえってトラブルに…」という声も。よくある失敗例と、専門的な視点からの解説です。
                </Typography>
                <Paper elevation={2} sx={{ p: 2, mb: 2, bgcolor: '#fffde7' }}>
                    <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <ReportProblem color="warning" sx={{ mr: 1 }} />
                        典型的な失敗例
                    </Typography>
                    <List dense>
                        <ListItem>
                            <ListItemIcon>
                                <ArrowRight color="disabled" />
                            </ListItemIcon>
                            <ListItemText
                                primary="吸音パネルだけ貼って満足"
                                secondary="吸音材は「反響音」を減らすもの。隙間風やドア下の「すきま音」にはほぼ無力です。"
                            />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon>
                                <ArrowRight color="disabled" />
                            </ListItemIcon>
                            <ListItemText
                                primary="隙間テープを貼り忘れ"
                                secondary="ドアや窓の隙間が最大の音漏れポイント。見落としがちな場所を要チェック！"
                            />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon>
                                <ArrowRight color="disabled" />
                            </ListItemIcon>
                            <ListItemText
                                primary="安価な耳栓だけで解決しようとする"
                                secondary="周囲には防音効果がないため、他の住人とのトラブルが解消しないことも…"
                            />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon>
                                <ArrowRight color="disabled" />
                            </ListItemIcon>
                            <ListItemText
                                primary="DIYで壁を傷つけてしまう"
                                secondary="原状回復できない施工は、退去時のトラブルに直結。賃貸では要注意です。"
                            />
                        </ListItem>
                    </List>
                </Paper>
                <Divider sx={{ my: 2 }} />
            </Box>

            {/* 効果的な使い方 */}
            <Box id="tips" sx={{ mb: 5 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    防音対策を最大限活かすコツ
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                    防音グッズやDIYだけでなく、<b>ちょっとした意識</b>や<em>コミュニケーション</em>も大切です。快適さを高めるための「コツ」をご紹介します。
                </Typography>
                <List>
                    <ListItem>
                        <ListItemIcon>
                            <TipsAndUpdates color="primary" />
                        </ListItemIcon>
                        <ListItemText
                            primary="音が気になる時間帯を共有"
                            secondary="夜間・早朝など、お互いに「静かにしてほしい時間」を事前に話し合っておきましょう。"
                        />
                    </ListItem>
                    <ListItem>
                        <ListItemIcon>
                            <TipsAndUpdates color="primary" />
                        </ListItemIcon>
                        <ListItemText
                            primary="定期的な「音チェック」"
                            secondary="防音アイテムを設置した後、実際にどれだけ効果があるか部屋を移動して確認。必要に応じて調整しましょう。"
                        />
                    </ListItem>
                    <ListItem>
                        <ListItemIcon>
                            <TipsAndUpdates color="primary" />
                        </ListItemIcon>
                        <ListItemText
                            primary="複数の対策を組み合わせて使う"
                            secondary="隙間テープ＋カーテン＋ジョイントマットなど、異なるアイテムを併用することで効果は格段にアップします。"
                        />
                    </ListItem>
                    <ListItem>
                        <ListItemIcon>
                            <TipsAndUpdates color="primary" />
                        </ListItemIcon>
                        <ListItemText
                            primary="「音を出す部屋」を決める"
                            secondary="楽器やオンライン会議など、音が出やすい用途は事前に部屋割りや時間配分を話し合うと◎。"
                        />
                    </ListItem>
                </List>
                <Box sx={{ width: '100%', my: 3 }}>
                    
                </Box>
                <Divider sx={{ my: 2 }} />
            </Box>

            {/* まとめ */}
            <Box id="summary" sx={{ mb: 5 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    まとめ：快適なルームシェアのために
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                    ルームシェアの防音対策は、「グッズ×DIY×コミュニケーション」の三本柱が決め手。ちょっとした工夫や気配りで、音のトラブルは大きく減らせます。この記事で紹介したアイデアを、ぜひあなたの生活に取り入れてみてください。<b>快適なシェアライフ</b>の実現を応援しています！
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', mt: 2 }}>
                    <Avatar src="/images/avatars/share-expert.jpg" alt="ルームシェア防音アドバイザーのプロフィール画像" sx={{ mr: 2 }} />
                    <Box>
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                            この記事を書いた人
                        </Typography>
                        <Typography variant="body2">
                            ルームシェア防音アドバイザー
                        </Typography>
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};

const post = {
    id: 'roomshare-soundproof-goods-ideas',
    slug: 'roomshare-soundproof-goods-ideas',
    title: 'ルームシェアの「防音対策」グッズ＆アイデア集',
    excerpt: 'ルームシェアで役立つおすすめ防音グッズやDIY防音アイデア、トラブル事例、効果的な使い方を専門家目線で徹底解説します。',
    coverImage: '/images/blog/cover-roomshare-soundproof_roomshare-soundproof-goods-ideas.jpg',
    coverImageAlt: '明るい自然光が差し込むモダンなルームシェアの共有リビングで、多様な背景を持つルームメイトたちが楽しく会話をしている様子。防音アイテムもさりげなく配置され、快適な空間が伝わる雰囲気',
    publishedAt: '2024-06-11T00:00:00.000Z',
    isPublished: true,
    categories: ['ルームシェア', '防音', '生活アイデア'],
    tags: ['防音グッズ', 'シェアハウス', 'DIY', '暮らしのコツ'],
    author: {
        name: 'ルームシェア防音アドバイザー',
        avatar: '/images/avatars/share-expert.jpg',
        bio: 'ルームシェアや共同生活の音トラブル対策に詳しいアドバイザー。現場のアイデアと最新グッズ情報をわかりやすく発信中。',
        profilePicture: '/images/avatars/share-expert.jpg'
    },
    viewCount: 1280,
    contentComponent: PostContent
};

export default post;